import React, { useEffect, useRef, useState } from "react";
import PropTypes from 'prop-types';
import Button from 'react-bootstrap/Button';
import Offcanvas from 'react-bootstrap/Offcanvas';
import { getCommitDetails, getFileLOCDetails } from '../../../services/apiServices';

import { AgGridReact } from 'ag-grid-react';
import '@ag-grid-community/styles/ag-grid.css';
import '@ag-grid-community/styles/ag-theme-quartz.css';
import { commitDetailsColDef, defaultColDef } from "../../../helpers/GitMetricColumnDef";
import CommitFileLOCDetails from "./CommitFileLOCDetails";
import ExportCommitPrToExcelButton from "../../common/ExportPrToExcelButton";
import { Col, Row, Spinner } from "react-bootstrap";
import { processCommitForExport } from "../../../helpers/DataHelper";
import moment from "moment";
import CommitLineChart from "../../Reports/commit/CommitLineChart";

const CommitDetails = ({ usersMetrics, platform, startEndDates }) => {
    const [isLoadingCommits, setIsLoadingCommits] = useState(false);
    const [show, setShow] = useState(false);
    const [showTable, setShowTable] = useState(false);
    const [commitDetails, setCommitDetails] = useState([]);
    const [fileLOC, setFileLOC] = useState([]);
    const [displayedCommit, setDisplayedCommit] = useState({});
    const [commitColDef, setCommitColDef] = useState(commitDetailsColDef);

    const hasAddedColumn = useRef(false);

    useEffect(() => {
        if (
            platform === "AzureDevOps" &&
            !hasAddedColumn.current
        ) {
            const colDef = [...commitDetailsColDef];
            colDef.splice(2, 0, {
                field: "changesCountModified",
                headerName: "# LOC: Modified",
                headerTooltip: "Number of Modified Lines",
                filter: "agNumberColumnFilter",
                width: 110,
                type: "numericColumn",
                aggFunc:'sum',
                suppressAggFuncInHeader: true,
                valueGetter: (params) => {
                    const total = Number(params.data?.totalChanges || 0);
                    const removed = Number(params.data?.removedChanges || 0);
                    const added = Number(params.data?.addedChanges || 0);
                    return total - removed - added;
                },
                cellRenderer: changesCountModifiedRenderer
            });
            // colDef.splice(3, 0, {
            //     field: "SuspiciousLines",
            //     headerName: "# Likely Bulk Paste",
            //     headerTooltip: "",
            //     filter: "agNumberColumnFilter",
            //     width: 160,
            //     type: "numericColumn",
            //     aggFunc:'sum',
            //     suppressAggFuncInHeader: true,
            //     valueGetter: (params) => {
            //         const adjustedLoc = Number(params.data?.suspectedLinesCount || 0);
            //         return adjustedLoc;
            //     },
            //     cellRenderer: suspiciousLinesRenderer
            // });

            setCommitColDef(colDef);
            hasAddedColumn.current = true;
        } else if (platform !== "AzureDevOps") {
            setCommitColDef(commitDetailsColDef);
            hasAddedColumn.current = false;
        }
    }, [platform, commitColDef]);

    const onRowClicked = async (params) => {
        const commitId = params.data.ID;
        let getFileWiseLOC = await getFileLOCDetails({ commitId: Number(commitId) });
        setFileLOC(getFileWiseLOC);
        setDisplayedCommit(params.data);
        setShowTable(true);
    };

    useEffect(() => {
        commitColDef.forEach(column => {
            if (column["field"] === "totalfileschanged") {
                column["cellRenderer"] = fileChangesRenderer
            }
            if (column["field"] === "changesCountModified") {
                column["cellRenderer"] = changesCountModifiedRenderer
            }
            if (column["field"] === "removedChanges") {
                column["cellRenderer"] = changesCountRemovedRenderer
            }
            if (column["field"] === "totalChanges") {
                column["cellRenderer"] = changesCountTotalRenderer
            }
            if (column["field"] === "hash") {
                column["cellRenderer"] = commitHashRenderer;
            }
            
        });
    }, [platform, commitColDef])

    function fileChangesRenderer(params) {
        const commitDetails = params["data"]
        const handleCommitMessageClick = async (commitId) => {
            let getFileWiseLOC = await getFileLOCDetails({ commitId: Number(commitId) });
            setFileLOC(getFileWiseLOC);
            setDisplayedCommit(commitDetails);
            setShowTable(true);
        }

        return (
            <>
                {Number(commitDetails["totalfileschanged"]) > 0 && <Button variant="link" onClick={() => handleCommitMessageClick(commitDetails.ID)}>
                    {commitDetails["totalfileschanged"]}
                </Button>}
            </>
        )
    }

    function commitHashRenderer(params) {
        const commitLink = params.data.commentLink
        // console.log(params.data.commitLink)
        const commitHash = params.value;
        if (!commitHash) return commitHash;
        if (!commitLink) return commitHash

        const openInCenteredWindow = () => {
            const width = 800;
            const height = 600;
            const left = (window.screen.width - width) / 2;
            const top = (window.screen.height - height) / 2;

            window.open(commitLink, '_blank', `width=${width},height=${height},left=${left},top=${top}`);
        };

        return (
            <a href={commitLink} target="_blank" rel="noreferrer" onClick={(e) => {
                e.preventDefault(); // Prevent default link behavior
                openInCenteredWindow(); // Open in centered window
            }}>
                {commitHash}
            </a>
        );
    }

    const handleClose = () => {
        setShow(false);
        setShowTable(false);
    };
    const handleShow = () => {
        setShow(true)
        handleCommitCountClick(usersMetrics["commitIds"]);
    };

    const handleCommitCountClick = async (commitIds) => {
        setIsLoadingCommits(true); // Start spinner
        try {
            let allCommitsDetails = await getCommitDetails({ commitIds });
            setCommitDetails(allCommitsDetails);
        } catch (error) {
            console.error("Failed to load commit details:", error);
        } finally {
            setIsLoadingCommits(false); // Hide spinner
        }
    };

    return (
        <>
            {usersMetrics["totalCommits"] > 0 && <Button variant="link" onClick={handleShow}>
                {usersMetrics["totalCommits"]}
            </Button>}
            {usersMetrics["totalCommits"] === 0 && <span>{usersMetrics["totalCommits"]}</span>}
            <Offcanvas show={show} onHide={handleClose} placement="end" className="commit-list-wrapper">
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title className="w-100">
                        <Row className="align-items-center" as={"div"}>
                            <Col xs={10} className="text-truncate">
                                <span className="text-nowrap">Commits By {usersMetrics["developer"]}</span>
                            </Col>
                            <Col xs={2} className="d-flex justify-content-end align-items-center">
                                <ExportCommitPrToExcelButton
                                    titleData={`Commits by ${usersMetrics["developer"]} For Date Range ${moment(startEndDates.startDate).format('ll')} to ${moment(startEndDates.endDate).format('ll')}`}
                                    data={commitDetails} // Pass the actual data here
                                    filename={`Commit-Details:${usersMetrics["developer"].replace(" ", "")}`}
                                    sheetName="Commit-Details"
                                    headers={commitColDef.map(detail => detail.headerName).filter(detail => detail !== "View")}
                                    processRowDataArray={processCommitForExport}
                                />&nbsp;&nbsp;&nbsp;
                            </Col>
                        </Row>
                    </Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body className="commit-details-offcanvas-body">
                    <Col className={`ag-theme-quartz ${showTable ? "commit-half-detail-grid" : "commit-full-detail-grid"}`}>
                        {isLoadingCommits ? (
                            <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
                                <Spinner animation="border" variant="primary" />
                            </div>
                        ) : (
                            <AgGridReact
                                rowData={commitDetails}
                                columnDefs={commitColDef}
                                defaultColDef={defaultColDef}
                                getRowId={(params) => params.data.ID.toString()}
                                rowSelection={"single"}
                                onRowClicked={onRowClicked}
                                className="ag-grid-style"
                            />
                        )}
                    </Col>
                    {showTable && <CommitFileLOCDetails
                        fileLOC={fileLOC}
                        hideTable={() => setShowTable(false)}
                        commitMessage={displayedCommit.message}
                        platform={platform}
                    />}
                    <CommitLineChart
                        data={commitDetails}
                    />
                </Offcanvas.Body>
            </Offcanvas>
        </>
    )
}

function changesCountModifiedRenderer(params) {

    let modifiedLOC = (params["data"].totalChanges || 0)
        - (params["data"].removedChanges || 0)
        - (params["data"].addedChanges || 0);
    return (
        <div>
            {`${modifiedLOC}`}
        </div>
    )
}

function changesCountRemovedRenderer(params) {
    if (params.value && params.value !== null) {
        let withoutDeleteRemoved = (Number(params["data"].removedChanges) || 0) - (Number(params["data"].filedeletedlineschanges) || 0);
        if (Number((params["data"].filedeletedlineschanges) || 0) === 0) {
            return (
                <div>
                    {`${params["data"].removedChanges || 0}`}
                </div>
            )
        }
        return (
            <div>
                {`${withoutDeleteRemoved} (${params["data"].filedeletedlineschanges || 0})`}
            </div>
        )
    }
    return (
        <div>0</div>
    );
}

function changesCountTotalRenderer(params) {
    if (params.value && params.value !== null) {
        let withoutDeleteRemoved = (Number(params["data"].totalChanges) || 0) - (Number(params["data"].filedeletedlineschanges) || 0);
        return (
            <div>
                {`${withoutDeleteRemoved}`}
            </div>
        )
    }
    return (
        <div>0</div>
    );
}

function suspiciousLinesRenderer(params) {
    if (params.node?.group) {
        return <div>{params.value}</div>;
    }
    if (!params.data) return null;
    return <div>{Number(params.data?.suspectedLinesCount || 0)}</div>;
}

CommitDetails.propTypes = {
    usersMetrics: PropTypes.object.isRequired,
    platform: PropTypes.string.isRequired,
    startEndDates: PropTypes.object.isRequired
}

export default CommitDetails;